// © 2026 Buddy Media LLC. All rights reserved.
//
// T-678 Phase 3 prep — magic-link consumer.
//
// The MCP server (T-679) emails Marc a link of the shape
// `https://admin.buddymedia.online/admin-login?token=XXXX` after he
// requests it via the request-link form (a separate route ships in
// a follow-up; for now the MCP `/admin/auth/request-link` endpoint
// is hit from a curl).
//
// This page reads the token query param, POSTs it to MCP's
// `/admin/auth/consume-link`, stores the returned JWT into the auth
// store, and redirects to /cluster-pulse. On any failure it surfaces
// the error and offers a link back to /login (worker-bearer paste).

import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";

// MCP backend URL. Defaults to the dev-tier hostname; override at
// build time via VITE_MCP_BACKEND_URL when the SPA is deployed
// against test/prod MCP instances.
const MCP_URL =
  (import.meta.env.VITE_MCP_BACKEND_URL as string | undefined) ??
  "https://dev-mcp.buddymedia.online";

type ConsumeResponse = {
  access_token: string;
  expires_at: string;
  user: {
    email: string;
    display_name: string | null;
    is_super: boolean;
  };
};

export function AdminLoginPage() {
  const setBearer = useAuthStore((s) => s.setBearer);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState<
    "idle" | "consuming" | "success" | "error" | "request-form" | "request-sent"
  >("idle");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    const token = params.get("token");
    if (!token) {
      // No token → show request-link form so the operator can ask
      // for one without leaving the SPA.
      setStatus("request-form");
      return;
    }
    setStatus("consuming");

    (async () => {
      try {
        const r = await fetch(`${MCP_URL}/admin/auth/consume-link`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        if (!r.ok) {
          let detail = "";
          try {
            const j = (await r.json()) as { detail?: string; message?: string };
            detail = j.detail ?? j.message ?? "";
          } catch {
            detail = await r.text();
          }
          setStatus("error");
          setErrMsg(
            r.status === 410
              ? "Magic link expired or already used. Request a new one."
              : `MCP rejected the token (HTTP ${r.status})${detail ? `: ${detail.slice(0, 200)}` : ""}`,
          );
          return;
        }
        const body = (await r.json()) as ConsumeResponse;
        setBearer(body.access_token);
        setStatus("success");
        // Brief pause so the success state is visible, then redirect.
        setTimeout(() => navigate("/cluster-pulse", { replace: true }), 400);
      } catch (ex) {
        setStatus("error");
        setErrMsg(
          ex instanceof Error
            ? `Network error reaching MCP: ${ex.message}`
            : "Network error reaching MCP",
        );
      }
    })();
  }, [params, setBearer, navigate]);

  const submitRequest = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setRequesting(true);
    setErrMsg(null);
    try {
      const r = await fetch(`${MCP_URL}/admin/auth/request-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      // Don't leak whether email is registered — succeed-shape on 4xx,
      // only show error on real 5xx.
      if (r.status >= 500) {
        let detail = "";
        try {
          const j = (await r.json()) as { detail?: string };
          detail = j.detail ?? "";
        } catch {
          /* ignore */
        }
        setErrMsg(
          `MCP server error (HTTP ${r.status})${detail ? `: ${detail.slice(0, 200)}` : ""}`,
        );
      } else {
        setStatus("request-sent");
      }
    } catch (ex) {
      setErrMsg(
        ex instanceof Error
          ? `Network error reaching MCP: ${ex.message}`
          : "Network error reaching MCP",
      );
    } finally {
      setRequesting(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center px-4">
      <div
        className="w-full max-w-md rounded-xl border p-6"
        style={{
          borderColor: "var(--bw-border)",
          background: "var(--bw-surface)",
        }}
      >
        <div className="mb-4 flex items-center gap-2">
          <span
            aria-hidden
            className="inline-block h-2.5 w-2.5 rounded-full"
            style={{
              background:
                "linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-accent) 100%)",
            }}
          />
          <h1 className="text-xl font-semibold text-white">Magic-link sign in</h1>
        </div>

        {status === "consuming" && (
          <p className="text-sm text-slate-300">Verifying your magic link…</p>
        )}

        {status === "success" && (
          <p className="text-sm text-emerald-300">
            Signed in. Redirecting to cluster pulse…
          </p>
        )}

        {status === "request-form" && (
          <>
            <p className="mb-5 text-sm text-slate-400">
              Enter your admin email — MCP sends a one-time sign-in link
              that expires in 15 minutes.
            </p>
            <form onSubmit={submitRequest} className="space-y-4">
              <label className="block">
                <span className="mb-1 block text-xs text-slate-400">Email</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="marc@buddywatch.online"
                  className="w-full rounded-md border bg-slate-900/80 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/40"
                  style={{ borderColor: "var(--bw-border)" }}
                  autoFocus
                  autoComplete="email"
                />
              </label>
              {errMsg && (
                <p className="text-sm text-rose-300" role="alert">
                  {errMsg}
                </p>
              )}
              <button
                type="submit"
                disabled={requesting || !email.trim()}
                className="w-full rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:brightness-110 disabled:opacity-50"
                style={{
                  background:
                    "linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-accent) 100%)",
                }}
              >
                {requesting ? "Sending…" : "Send magic link"}
              </button>
            </form>
            <p className="mt-5 text-xs text-slate-500">
              Or{" "}
              <Link to="/login" className="underline hover:text-slate-300">
                sign in with bearer token
              </Link>{" "}
              (workers).
            </p>
          </>
        )}

        {status === "request-sent" && (
          <>
            <p className="mb-3 text-sm text-emerald-300">
              Magic link sent. Check {email} — link expires in 15 minutes.
            </p>
            <p className="text-xs text-slate-500">
              Click the link in the email to finish signing in.
            </p>
          </>
        )}

        {status === "error" && (
          <>
            <p className="mb-4 text-sm text-rose-300" role="alert">
              {errMsg}
            </p>
            <Link
              to="/login"
              className="inline-block rounded-md px-4 py-2 text-sm font-medium text-white transition-all hover:brightness-110"
              style={{
                background:
                  "linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-accent) 100%)",
              }}
            >
              Sign in with bearer token instead
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
